import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Bell, Calendar, FileSignature, GitBranch, MessageSquare, UserPlus, AlertCircle,
    CheckCheck, Trash2, RefreshCw, Settings,
} from "lucide-react";

const EVENT_ICONS = {
    interview_scheduled: Calendar,
    interview_rescheduled: Calendar,
    interview_cancelled: Calendar,
    offer_sent: FileSignature,
    stage_milestone: GitBranch,
    recruiter_assigned: UserPlus,
    ticket_reply: MessageSquare,
    ticket_new: MessageSquare,
    generic: AlertCircle,
};

const EVENT_LABELS = {
    interview_scheduled: "Interview scheduled",
    interview_rescheduled: "Interview rescheduled",
    interview_cancelled: "Interview cancelled",
    offer_sent: "Offer sent",
    stage_milestone: "Application milestone",
    recruiter_assigned: "Assigned to a candidate",
    ticket_reply: "Support reply",
    ticket_new: "New support ticket",
    generic: "Notification",
};

/**
 * /notifications — full history page with mark-read, clear-all, and a
 * preferences side panel that calls the same endpoints as the seeker settings
 * (so recruiter + admin + interviewer + seeker share one prefs UI).
 */
export default function NotificationsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");  // all | unread
    const [showPrefs, setShowPrefs] = useState(false);
    const nav = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 200, only_unread: filter === "unread" } });
            setItems(data || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const onItemClick = async (n) => {
        try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch { /* silent */ }
        if (n.link) nav(n.link);
        else load();
    };

    const onMarkAll = async () => {
        try {
            await api.post("/notifications/read-all");
            toast.success("All marked as read");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onClearAll = async () => {
        if (!window.confirm("Delete all READ notifications? Unread ones are kept.")) return;
        try {
            const { data } = await api.delete("/notifications/clear");
            toast.success(`Cleared ${data?.deleted ?? 0} read notification${(data?.deleted ?? 0) === 1 ? "" : "s"}`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-screen-lg mx-auto px-3 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Bell className="w-5 h-5 text-ikb" />
                <h1 className="text-2xl font-display font-bold tracking-tight">Notifications</h1>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <Button onClick={onMarkAll} variant="outline" size="sm" className="rounded-none" data-testid="notif-mark-all-page">
                        <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read
                    </Button>
                    <Button onClick={onClearAll} variant="outline" size="sm" className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="notif-clear-page">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear read
                    </Button>
                    <Button onClick={() => setShowPrefs((v) => !v)} variant="outline" size="sm" className="rounded-none" data-testid="notif-prefs-toggle">
                        <Settings className="w-3.5 h-3.5 mr-1" /> Preferences
                    </Button>
                    <Button onClick={load} variant="outline" size="sm" className="rounded-none" disabled={loading} data-testid="notif-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <button
                    onClick={() => setFilter("all")}
                    className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${filter === "all" ? "border-ikb bg-ikb text-white font-bold" : "border-border bg-white hover:bg-secondary text-foreground/70"}`}
                    data-testid="notif-filter-all"
                >
                    All
                </button>
                <button
                    onClick={() => setFilter("unread")}
                    className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${filter === "unread" ? "border-ikb bg-ikb text-white font-bold" : "border-border bg-white hover:bg-secondary text-foreground/70"}`}
                    data-testid="notif-filter-unread"
                >
                    Unread
                </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <div className={`${showPrefs ? "md:col-span-2" : "md:col-span-3"}`}>
                    {loading && items.length === 0 ? (
                        <div className="text-foreground/60 text-sm py-12 text-center">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="border border-dashed border-border p-8 text-center text-foreground/60" data-testid="notif-empty">
                            {filter === "unread" ? "Nothing unread." : "No notifications yet — events from your team will show up here."}
                        </div>
                    ) : (
                        <div className="border border-border bg-white" data-testid="notif-list">
                            {items.map((n) => {
                                const Icon = EVENT_ICONS[n.event] || EVENT_ICONS.generic;
                                return (
                                    <button
                                        key={n.id}
                                        onClick={() => onItemClick(n)}
                                        className={`w-full text-left px-3 py-3 hover:bg-secondary/40 border-b border-border last:border-b-0 flex items-start gap-3 ${n.read ? "" : "bg-ikb/5"}`}
                                        data-testid={`notif-row-${n.id}`}
                                    >
                                        <div className={`mt-0.5 ${n.read ? "text-foreground/40" : "text-ikb"}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className={`text-[10px] font-mono uppercase tracking-widest ${n.read ? "text-foreground/40" : "text-ikb"}`}>
                                                {EVENT_LABELS[n.event] || n.event}
                                            </div>
                                            <div className={`text-sm ${n.read ? "text-foreground/70" : "font-medium text-foreground"}`}>
                                                {n.title}
                                            </div>
                                            <div className="text-[12px] text-foreground/60 mt-0.5">{n.body}</div>
                                            <div className="text-[10px] text-foreground/40 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                                        </div>
                                        {!n.read ? <span className="w-2 h-2 bg-ikb rounded-full shrink-0 mt-1.5" /> : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {showPrefs ? <NotificationPrefsPanel /> : null}
            </div>
        </div>
    );
}

/**
 * Right-side preferences panel — same UI used by seeker settings. Renders the
 * full matrix of (event × channel) toggles fetched from /notification-prefs.
 */
export function NotificationPrefsPanel() {
    const [prefs, setPrefs] = useState({ events: {}, event_labels: {}, channels: ["in_app", "email"] });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/notification-prefs");
            setPrefs(data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (event, channel, val) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/notification-prefs", { events: { [event]: { [channel]: val } } });
            setPrefs((p) => ({ ...p, events: data.events }));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-border bg-white p-3" data-testid="notif-prefs-panel">
            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 border-b border-border pb-2 mb-3">Notification preferences</div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                        <th className="text-left pb-1">Event</th>
                        {(prefs.channels || []).map((ch) => (
                            <th key={ch} className="text-center pb-1 px-1">{ch === "in_app" ? "In-app" : ch}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(prefs.event_labels || {}).map(([ev, label]) => {
                        if (ev === "generic") return null;
                        return (
                            <tr key={ev} className="border-t border-border/50">
                                <td className="py-1.5 pr-2 text-foreground/80">{label}</td>
                                {(prefs.channels || []).map((ch) => {
                                    const val = !!(prefs.events?.[ev]?.[ch] ?? true);
                                    return (
                                        <td key={ch} className="text-center py-1.5">
                                            <input
                                                type="checkbox"
                                                checked={val}
                                                disabled={saving}
                                                onChange={(e) => toggle(ev, ch, e.target.checked)}
                                                className="cursor-pointer accent-[#002FA7]"
                                                data-testid={`pref-toggle-${ev}-${ch}`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="text-[10px] text-foreground/40 mt-3">Changes are saved automatically.</div>
        </div>
    );
}
